import { forwardRef, useEffect, type ElementRef } from "react";

import { cn } from "../../lib";
import { DialogBodyPrimitive } from "../../primitives";
import { dialogSectionPadding, useDialogContext } from "./lib";
import type { DialogBodyProps } from "./types";

/**
 * Dialog.Body
 *
 * Scrolling middle region: takes the height its content needs and scrolls
 * once the surface reaches its maximum (85dvh web, 85% native), with header
 * and footer pinned. The scroll mechanics are per platform, in
 * `DialogBodyPrimitive`; this adds the section padding, and tells the Footer
 * that it's there and whether its content scrolls (the Footer shows its
 * divider then).
 */
export const DialogBody = forwardRef<ElementRef<typeof DialogBodyPrimitive>, DialogBodyProps>(
  ({ children, className }, ref) => {
    const { setBodyOverflowing, setHasBody } = useDialogContext();
    // Tells the Footer a Body sits above it (see Dialog.Footer)
    useEffect(() => {
      setHasBody(true);
      return () => setHasBody(false);
    }, [setHasBody]);
    return (
      <DialogBodyPrimitive
        ref={ref}
        className={cn(dialogSectionPadding, className)}
        onOverflowChange={setBodyOverflowing}
      >
        {children}
      </DialogBodyPrimitive>
    );
  },
);

DialogBody.displayName = "Dialog.Body";
